"use client";

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

/**
 * ScrollRestoration component to handle smooth scrolling with fixed elements
 * This component works with your existing CSS setup and prevents scroll warnings
 */
export function ScrollRestoration() {
  const pathname = usePathname();

  useEffect(() => {
    const handleRouteChange = () => {
      // Check for fixed positioned elements that might interfere with scroll
      const fixedElements = document.querySelectorAll('.fixed, [class*="fixed"]');
      const stickyElements = document.querySelectorAll('.sticky, [class*="sticky"]');
      
      if (fixedElements.length > 0 || stickyElements.length > 0) {
        // Delay scroll to top to avoid conflicts with fixed/sticky elements
        setTimeout(() => {
          window.scrollTo({ 
            top: 0, 
            behavior: 'smooth' 
          });
        }, 100);
      } else {
        // Immediate scroll if no fixed elements
        window.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
      }
    };

    // Handle route changes
    handleRouteChange();
  }, [pathname]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        window.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
      }, 50);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return null;
}

/**
 * Hook for managing scroll behavior in components with fixed elements
 * Use this in components that have fixed positioned notifications or dialogs
 */
export function useScrollBehavior() {
  useEffect(() => {
    // Prevent scroll restoration conflicts with fixed elements
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Custom scroll handling for fixed elements
    const handleBeforeUnload = () => {
      // Reset scroll position before page unload
      window.scrollTo(0, 0);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Restore default scroll restoration
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    };
  }, []);
}

/**
 * Enhanced notification component that doesn't interfere with scroll
 * Use this pattern for your fixed positioned notifications
 */
export function ScrollFriendlyNotification({ 
  isVisible, 
  onClose, 
  title, 
  content,
  className = ""
}: {
  isVisible: boolean;
  onClose: () => void;
  title: string;
  content: string;
  className?: string;
}) {
  // Use the scroll behavior hook
  useScrollBehavior();

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed-notification fixed bottom-4 right-4 left-4 max-[419px]:w-auto max-[419px]:mx-2 z-20 bg-white shadow-lg rounded-lg p-4 max-w-sm w-full animate-fade-in-up ${className}`}
      role="dialog"
      aria-labelledby="notification-title"
      aria-describedby="notification-text"
      style={{
        // Prevent interference with Next.js scroll behavior
        overscrollBehavior: 'contain',
        // Ensure proper stacking without interfering with scroll
        isolation: 'isolate'
      }}
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 id="notification-title" className="font-semibold text-gray-900">
            {title}
          </h3>
          <p id="notification-text" className="text-sm text-gray-600 mt-1">
            {content}
          </p>
        </div>
        <button
          onClick={onClose}
          className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close notification"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}

